import { useState, useEffect } from 'react';
import {
  Shield,
  Settings,
  Power,
  PowerOff,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import api from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';
import { useTenantStore } from '../../stores/tenantStore';

interface WireGuardServerConfig {
  id: string;
  enabled: boolean;
  listenPort: number;
  interfaceAddress: string;
  publicKey: string;
  dnsServers: string[];
  mtu: number;
  endpointHost: string | null;
  sourceInterface: string | null;
  createdAt: string;
  updatedAt: string;
}

interface LogicalInterface {
  id: string;
  name: string;
  ipv4: string | null;
  subnet: string | null;
  dhcp_client: boolean;
  operationalState: string;
}

interface WireGuardConfigProps {
  edgeId: string;
  onConfigChange?: () => void;
}

const WireGuardConfig = ({ edgeId, onConfigChange }: WireGuardConfigProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [configured, setConfigured] = useState(false);
  const [config, setConfig] = useState<WireGuardServerConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    enabled: true,
    listenPort: 51820,
    interfaceAddress: '10.200.0.1/24',
    dnsServers: '',
    mtu: 1420,
    endpointHost: '',
    sourceInterface: '',
  });

  // Confirm dialog state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const fetchLogicalInterfaces = async () => {
    setLoadingInterfaces(true);
    try {
      // Don't include /api/v1 in URL - baseURL already has it
      const response = await api.get(`/edges/${edgeId}/interfaces/logical`);
      const interfaces = response.data.items || [];
      // Show all interfaces with an IP address (DHCP or static).
      // In TAP mode, the agent overrides the source address to the TAP link-local IP.
      const availableInterfaces = interfaces.filter((iface: LogicalInterface) => iface.ipv4);
      setLogicalInterfaces(availableInterfaces);
    } catch (err: any) {
      console.error('Failed to load logical interfaces:', err);
    } finally {
      setLoadingInterfaces(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v2/edges/${edgeId}/wireguard/config`);
      const data = response.data;

      setConfigured(data.configured);
      if (data.configured && data.config) {
        setConfig(data.config);
        setFormData({
          enabled: data.config.enabled,
          listenPort: data.config.listenPort,
          interfaceAddress: data.config.interfaceAddress,
          dnsServers: data.config.dnsServers?.join(', ') || '',
          mtu: data.config.mtu || 1420,
          endpointHost: data.config.endpointHost || '',
          sourceInterface: data.config.sourceInterface || '',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load WireGuard configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchLogicalInterfaces();
  }, [edgeId, selectedTenantId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate source interface selection
      if (!formData.sourceInterface) {
        setError('Please select a source interface');
        setSaving(false);
        return;
      }

      const payload = {
        enabled: formData.enabled,
        listenPort: formData.listenPort,
        interfaceAddress: formData.interfaceAddress,
        dnsServers: formData.dnsServers
          ? formData.dnsServers.split(',').map((s) => s.trim()).filter((s) => s)
          : [],
        mtu: formData.mtu,
        endpointHost: formData.endpointHost || undefined,
        sourceInterface: formData.sourceInterface,
      };

      await api.post(`/api/v2/edges/${edgeId}/wireguard/config`, payload);
      setSuccess('WireGuard server configured successfully');
      setShowForm(false);
      await fetchConfig();
      onConfigChange?.();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = () => {
    setShowDisableConfirm(true);
  };

  const confirmDisable = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/v2/edges/${edgeId}/wireguard/config`);
      setSuccess('WireGuard server disabled');
      setConfigured(false);
      setConfig(null);
      setShowDisableConfirm(false);
      onConfigChange?.();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to disable WireGuard');
    } finally {
      setSaving(false);
    }
  };

  const copyPublicKey = async () => {
    if (config?.publicKey) {
      await navigator.clipboard.writeText(config.publicKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading WireGuard configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">WireGuard Server</h3>
            <p className="text-sm text-gray-500">
              Enable this edge as a WireGuard VPN server for IoT gateways
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchConfig}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-1 px-3 py-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {configured ? (
            <button
              onClick={() => setShowForm(true)}
              className="btn-secondary inline-flex items-center space-x-1 px-3 py-1.5"
            >
              <Settings className="w-4 h-4" />
              <span>Edit Settings</span>
            </button>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary inline-flex items-center space-x-1 px-3 py-1.5"
            >
              <Power className="w-4 h-4" />
              <span>Enable WireGuard</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {/* Configuration Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
          <h4 className="text-md font-semibold text-gray-900">
            {configured ? 'Edit WireGuard Settings' : 'Enable WireGuard Server'}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Listen Port
              </label>
              <input
                type="number"
                value={formData.listenPort}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, listenPort: parseInt(e.target.value) || 51820 }))
                }
                min={1024}
                max={65535}
                className="input-base w-full"
                placeholder="51820"
              />
              <p className="text-xs text-gray-500 mt-1">UDP port for WireGuard (default: 51820)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tunnel Network (CIDR)
              </label>
              <input
                type="text"
                value={formData.interfaceAddress}
                onChange={(e) => setFormData((prev) => ({ ...prev, interfaceAddress: e.target.value }))}
                className="input-base w-full"
                placeholder="10.200.0.1/24"
              />
              <p className="text-xs text-gray-500 mt-1">This edge's IP in the WireGuard network</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source Interface (VPP Logical Interface) *
              </label>
              <select
                value={formData.sourceInterface}
                onChange={(e) => setFormData((prev) => ({ ...prev, sourceInterface: e.target.value }))}
                className="input-base w-full"
                disabled={loadingInterfaces}
              >
                <option value="">Select a logical interface...</option>
                {logicalInterfaces.map((iface) => (
                  <option key={iface.id} value={iface.name}>
                    {iface.name} ({iface.ipv4}/{iface.subnet}) - {iface.operationalState}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                VPP logical interface for WireGuard binding. Cloud edges (TAP mode) will auto-detect the correct source address.
              </p>
              {logicalInterfaces.length === 0 && !loadingInterfaces && (
                <p className="text-xs text-red-600 mt-1">
                  No interfaces with IP addresses found. Please ensure the edge has at least one configured logical interface.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Public Endpoint (optional)
              </label>
              <input
                type="text"
                value={formData.endpointHost}
                onChange={(e) => setFormData((prev) => ({ ...prev, endpointHost: e.target.value }))}
                className="input-base w-full"
                placeholder="203.0.113.5 or edge.example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Public IP or hostname for IoT gateways to connect to (defaults to source interface IP)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MTU</label>
              <input
                type="number"
                value={formData.mtu}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mtu: parseInt(e.target.value) || 1420 }))
                }
                min={1280}
                max={1500}
                className="input-base w-full"
                placeholder="1420"
              />
              <p className="text-xs text-gray-500 mt-1">WireGuard interface MTU (default: 1420)</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DNS Servers (optional)
              </label>
              <input
                type="text"
                value={formData.dnsServers}
                onChange={(e) => setFormData((prev) => ({ ...prev, dnsServers: e.target.value }))}
                className="input-base w-full"
                placeholder="8.8.8.8, 8.8.4.4"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated DNS servers for WireGuard clients
              </p>
            </div>

            <div className="md:col-span-2 flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData((prev) => ({ ...prev, enabled: e.target.checked }))}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                Enable WireGuard interface
              </label>
            </div>
          </div>

          <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary inline-flex items-center space-x-2 px-4 py-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{configured ? 'Save Changes' : 'Enable WireGuard'}</span>
            </button>
            <button
              onClick={() => setShowForm(false)}
              disabled={saving}
              className="btn-secondary px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Current Configuration Display */}
      {configured && config && !showForm && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {config.enabled ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Power className="w-3 h-3 mr-1" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <PowerOff className="w-3 h-3 mr-1" />
                  Disabled
                </span>
              )}
              <span className="text-sm text-gray-500">
                Interface: wg0 | Port: {config.listenPort}/UDP
              </span>
            </div>
            <button
              onClick={handleDisable}
              disabled={saving}
              className="text-red-600 hover:text-red-800 text-sm font-medium inline-flex items-center space-x-1"
            >
              <PowerOff className="w-4 h-4" />
              <span>Disable WireGuard</span>
            </button>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Source Interface
                </label>
                <p className="text-sm font-mono text-gray-900">{config.sourceInterface || 'G0 (default)'}</p>
                <p className="text-xs text-gray-500 mt-0.5">VPP logical interface</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Tunnel Network
                </label>
                <p className="text-sm font-mono text-gray-900">{config.interfaceAddress}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Endpoint
                </label>
                <p className="text-sm font-mono text-gray-900">
                  {config.endpointHost || '(Not configured)'}:{config.listenPort}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  MTU
                </label>
                <p className="text-sm text-gray-900">{config.mtu}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  DNS Servers
                </label>
                <p className="text-sm text-gray-900">
                  {config.dnsServers?.length > 0 ? config.dnsServers.join(', ') : '(None)'}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Server Public Key
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-sm font-mono bg-gray-100 px-3 py-2 rounded border border-gray-200 overflow-x-auto">
                  {config.publicKey}
                </code>
                <button
                  onClick={copyPublicKey}
                  className="btn-secondary px-3 py-2 inline-flex items-center space-x-1"
                  title="Copy public key"
                >
                  {copiedKey ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Share this key with IoT gateway administrators to configure their WireGuard clients
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Not Configured State */}
      {!configured && !showForm && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">WireGuard Not Configured</h4>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
            Enable WireGuard to allow IoT gateways (like GL.iNet Opal) to securely connect to this
            edge device.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Power className="w-4 h-4" />
            <span>Enable WireGuard</span>
          </button>
        </div>
      )}

      {/* Confirm Disable Dialog */}
      <ConfirmDialog
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={confirmDisable}
        title="Disable WireGuard"
        message="Are you sure you want to disable WireGuard? All peers will be disconnected."
        confirmText="Disable"
        variant="warning"
        isLoading={saving}
      />
    </div>
  );
};

export default WireGuardConfig;
